import { useMemo, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { Calculator } from './Calculator';
import { VirtualTrades } from './VirtualTrades';
import { ClaimCount, CountUp, Reveal, SectionHead } from './primitives';
import { useClaim } from './lib/siteContent';
import { useMarqueeSpeed } from './lib/marquee';
import { easeOut, viewportOnce } from './lib/motion';
import { ArrowUpRight, BarChart, Calendar, ChevronDown, Check, Eye, HelpCircle, Mail, Search, Send, TrendingUp, Wallet, } from './icons';
import smallSign from '../../../assets/logo/small-sign.svg';
import creditArt1 from '../../../assets/returns/pic-1.png';
import creditArt2 from '../../../assets/returns/pic-2.png';
import { useT } from './lib/siteTexts';
import './Returns.css';
/* Credit-leverage illustration candidates (assets/returns).
   Swap the index to try the other: 0 = cascading scales (pic-1), 1 = lever (pic-2). */
const CREDIT_ART = [creditArt1, creditArt2][0];
/* ---------- Hero visual: animated "trading terminal" card ----------
   Illustrates exactly what the page shows: the portfolio curve draws
   itself, buy/sell markers fire along it (like the entry-point
   scenarios below), the portfolio value counts up, +45%/yr badge. */
const HERO_LINE = 'M0 252 C40 244 60 250 90 232 S140 208 170 214 S220 178 250 186 S300 138 330 146 S380 96 410 104 S450 60 480 44';
const HERO_AREA = `${HERO_LINE} L480 300 L0 300 Z`;
const HERO_MARKS = [
    { x: 90, y: 232, kind: 'buy', label: 'chart_buy' },
    { x: 250, y: 186, kind: 'sell', label: 'chart_sell' },
    { x: 330, y: 146, kind: 'buy' },
    { x: 410, y: 104, kind: 'sell' },
];
function HeroChart() {
    const t = useT('returns.hero');
    return (<div className="rt-hchart">
      <div className="rt-hchart__head">
        <span className="rt-hchart__title">{t('chart_title')}</span>
        <span className="rt-hchart__live">
          <i className="pulse-dot" aria-hidden="true"/>
          {t('chart_live')}
        </span>
      </div>

      <div className="rt-hchart__plot">
        <svg viewBox="0 0 480 300" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="rtHeroFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#6ef2b0" stopOpacity="0.26"/>
              <stop offset="1" stopColor="#6ef2b0" stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="rtHeroLine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#7f97e8"/>
              <stop offset="1" stopColor="#6ef2b0"/>
            </linearGradient>
          </defs>
          {[60, 120, 180, 240].map((y) => (<line key={y} x1="0" x2="480" y1={y} y2={y} className="rt-hchart__grid"/>))}
          <motion.path d={HERO_AREA} fill="url(#rtHeroFill)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.7 }}/>
          <motion.path d={HERO_LINE} fill="none" stroke="url(#rtHeroLine)" strokeWidth="2.5" strokeLinecap="round" vectorEffect="non-scaling-stroke" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, ease: easeOut, delay: 0.5 }}/>
        </svg>
        {HERO_MARKS.map((m) => (<motion.span key={m.x} className={`rt-hchart__mark rt-hchart__mark--${m.kind}`} style={{ left: `${(m.x / 480) * 100}%`, top: `${(m.y / 300) * 100}%` }} initial={{ scale: 0, x: '-50%', y: '-50%' }} animate={{ scale: 1, x: '-50%', y: '-50%' }} transition={{
                delay: 0.5 + (m.x / 480) * 2,
                type: 'spring',
                stiffness: 320,
                damping: 18,
            }}>
            {m.label && <em>{t(m.label)}</em>}
          </motion.span>))}
        <motion.span className="rt-hchart__enddot" style={{ left: '100%', top: `${(44 / 300) * 100}%` }} initial={{ scale: 0, x: '-50%', y: '-50%' }} animate={{ scale: 1, x: '-50%', y: '-50%' }} transition={{ delay: 2.5, type: 'spring', stiffness: 300, damping: 16 }} aria-hidden="true"/>
      </div>

      <div className="rt-hchart__foot">
        <div className="rt-hchart__val">
          <span>{t('chart_value')}</span>
          <strong className="num">
            <CountUp to={14500} duration={2.2} delay={0.6} locale="en-US"/> $
          </strong>
        </div>
        <span className="rt-hchart__badge">
          <TrendingUp size={14}/>
          {t('chart_badge')}
        </span>
      </div>
    </div>);
}
const GRAPH_PERIODS = [
    {
        key: '3m',
        label: '3 міс.',
        pct: 9,
        endY: 204,
        spPct: 3,
        spEndY: 244,
        sp: 'M0 258 C40 258 60 257 90 256 S140 253 170 254 S220 251 250 252 S300 249 330 250 S380 247 410 248 S450 245 480 244',
        months: ['Січ', 'Лют', 'Бер'],
        line: 'M0 258 C40 261 60 251 90 254 S140 241 170 247 S220 233 250 239 S300 225 330 231 S380 217 410 223 S450 207 480 204',
    },
    {
        key: '6m',
        label: '6 міс.',
        pct: 19,
        endY: 146,
        spPct: 6,
        spEndY: 230,
        sp: 'M0 258 C40 257 60 258 90 254 S140 250 170 252 S220 246 250 248 S300 240 330 242 S380 236 410 236 S450 232 480 230',
        months: ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер'],
        line: 'M0 258 C40 253 60 257 90 246 S140 230 170 236 S220 208 250 214 S300 188 330 194 S380 168 410 172 S450 150 480 146',
    },
    {
        key: '1y',
        label: '1 рік',
        pct: 45,
        endY: 48,
        spPct: 12,
        spEndY: 202,
        sp: 'M0 258 C40 256 60 257 90 252 S140 246 170 248 S220 238 250 240 S300 228 330 230 S380 216 410 218 S450 206 480 202',
        months: ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру'],
        line: 'M0 258 C40 250 60 254 90 238 S140 214 170 220 S220 182 250 190 S300 142 330 148 S380 98 410 106 S450 62 480 48',
    },
];
function ReturnsGraph() {
    const [period, setPeriod] = useState('1y');
    const p = GRAPH_PERIODS.find((g) => g.key === period);
    const t = useT('returns.verify');
    const claim = useClaim();
    // Observe the full-size plot (not the tiny/def'd animated bits) and drive every
    // animation off that flag with `animate` — so a missed IntersectionObserver on a
    // 0-size or non-rendered element can't leave the line/badges stuck at their start.
    const plotRef = useRef(null);
    const drawn = useInView(plotRef, { once: true, margin: '-80px' });
    return (<Reveal className="rt-graph" variant="fadeUp">
      <div className="rt-graph__head">
        <span className="rt-graph__label">{t('graph_label')}</span>
        <div className="rt-graph__seg" role="group" aria-label="Період графіка">
          {GRAPH_PERIODS.map((g) => (<button key={g.key} type="button" className="rt-graph__seg-btn" aria-pressed={period === g.key} onClick={() => setPeriod(g.key)}>
              {g.label}
            </button>))}
        </div>
      </div>

      <div className="rt-graph__legend" aria-hidden="true">
        <span className="rt-graph__lg">
          <i className="rt-graph__lg-algo"/>
          {t('graph_ours')}
        </span>
        <span className="rt-graph__lg">
          <i className="rt-graph__lg-sp"/>
          S&amp;P 500
        </span>
      </div>

      <div className="rt-graph__plot" ref={plotRef}>
        <div className="rt-graph__canvas" aria-hidden="true">
          {/* static grid layer */}
          <svg viewBox="0 0 480 300" preserveAspectRatio="none">
            {[60, 120, 180, 240].map((y) => (<line key={y} x1="0" x2="480" y1={y} y2={y} className="rt-graph__grid"/>))}
          </svg>
          {/* data layer — continuous strokes revealed by a left→right wipe.
           (No dash-based pathLength draw: dasharray + non-scaling-stroke on a
           stretched viewBox renders the line broken in Chromium.) */}
          <div className="rt-graph__wipe">
            <svg viewBox="0 0 480 300" preserveAspectRatio="none">
              <defs>
                <linearGradient id="rtGraphLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#475ffa"/>
                  <stop offset="1" stopColor="#9e78ff"/>
                </linearGradient>
                <linearGradient id="rtGraphFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#7a5fff" stopOpacity="0.2"/>
                  <stop offset="1" stopColor="#7a5fff" stopOpacity="0"/>
                </linearGradient>
                {/* left→right reveal via a growing SVG clip rect — a reliable SVG
           attribute animation (unlike CSS clip-path), driven by `drawn`. */}
                <clipPath id={`rtGraphReveal-${p.key}`}>
                  <motion.rect key={p.key} x="0" y="0" height="300" initial={{ width: 0 }} animate={{ width: drawn ? 480 : 0 }} transition={{ duration: 1.3, ease: easeOut, delay: 0.15 }}/>
                </clipPath>
              </defs>
              <g clipPath={`url(#rtGraphReveal-${p.key})`}>
                <path d={`${p.line} L480 300 L0 300 Z`} fill="url(#rtGraphFill)"/>
                <path d={p.sp} className="rt-graph__spline"/>
                <path d={p.line} fill="none" stroke="url(#rtGraphLine)" strokeWidth="2.5" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
              </g>
            </svg>
          </div>
          <motion.span key={`d-${p.key}`} className="rt-graph__dot" style={{ left: '100%', top: `${(p.endY / 300) * 100}%` }} initial={{ scale: 0, x: '-50%', y: '-50%' }} animate={{ scale: drawn ? 1 : 0, x: '-50%', y: '-50%' }} transition={{ delay: 1.35, type: 'spring', stiffness: 300, damping: 16 }}/>
          <motion.span key={`p-${p.key}`} className="rt-graph__pct num" style={{ left: '100%', top: `${(p.endY / 300) * 100}%` }} initial={{ opacity: 0, scale: 0.8, x: '-108%', y: '-140%' }} animate={{ opacity: drawn ? 1 : 0, scale: drawn ? 1 : 0.8, x: '-108%', y: '-140%' }} transition={{ delay: 1.5, type: 'spring', stiffness: 280, damping: 18 }}>
            +{p.key === '1y' && claim ? claim.num : p.pct}%
          </motion.span>
          <motion.span key={`s-${p.key}`} className="rt-graph__sp-pct num" style={{ left: '100%', top: `${(p.spEndY / 300) * 100}%` }} initial={{ opacity: 0, x: '-106%', y: '-50%' }} animate={{ opacity: drawn ? 1 : 0, x: '-106%', y: '-50%' }} transition={{ delay: 1.6, duration: 0.35 }}>
            S&amp;P 500 · +{p.spPct}%
          </motion.span>
        </div>
        <motion.div key={`m-${p.key}`} className="rt-graph__months" aria-hidden="true" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.1 }}>
          {p.months.map((m) => (<span key={m}>{m}</span>))}
        </motion.div>
      </div>

      <p className="rt-graph__note">
        {t.rich('note_before')}{' '}
        <button type="button" className="rt-graph__phrase" aria-pressed={period === '3m'} onClick={() => setPeriod('3m')}>
          {t('note_3m')}
        </button>
        ,{' '}
        <button type="button" className="rt-graph__phrase" aria-pressed={period === '6m'} onClick={() => setPeriod('6m')}>
          {t('note_6m')}
        </button>
        ,{' '}
        <button type="button" className="rt-graph__phrase" aria-pressed={period === '1y'} onClick={() => setPeriod('1y')}>
          {t('note_1y')}
        </button>{' '}
        {t.rich('note_after')}
      </p>
    </Reveal>);
}
/* ---------- Data ---------- */
// Тексти — адмінка «Контент» → «Тексти сторінок» → «Дохідність» (returns.*).
const PRINCIPLE_KEYS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7'];
const TRUST_ICONS = [BarChart, Search, Eye];
const SCENARIO_N = [1, 2];
const EVENT_N = [1, 2, 3, 4];
/* Compound interest: 1.03^m for 12 months (final +42.6% vs simple +36%) */
const COMPOUND = Array.from({ length: 12 }, (_, i) => Math.pow(1.03, i + 1));
const COMPOUND_MAX = COMPOUND[11] - 1; // 0.4258
const COMP_TOP = 0.5; // chart ceiling → headroom above the 42.6% peak for its label
const COMP_TICKS = [0.1, 0.2, 0.3, 0.4]; // y-axis scale marks (10–40%)
/* ---------- Scenario timeline (draw-on-scroll) ---------- */
function ScenarioTimeline({ tag, events, index }) {
    // Draw is gated by a useInView flag + explicit `animate` rather than nested
    // whileInView: inside the <Reveal> variant parent, a child's whileInView doesn't
    // stop variant propagation, so the timeline never ran its own draw. `animate` does.
    const areaRef = useRef(null);
    const drawn = useInView(areaRef, viewportOnce);
    return (<Reveal className="rt-tl" variant="fadeUp" delay={index * 0.08}>
      <span className="rt-tl__tag">{tag}</span>
      <div className="rt-tl__area" ref={areaRef}>
        <div className="rt-tl__linewrap" style={{ '--n': events.length }} aria-hidden="true">
          <motion.div className="rt-tl__linefill" initial={{ width: '0%' }} animate={{ width: drawn ? '100%' : '0%' }} transition={{ duration: 1.5, ease: easeOut, delay: 0.2 + index * 0.15 }}/>
        </div>
        <div className="rt-tl__row" style={{ '--n': events.length }}>
          {events.map((e, i) => {
            const frac = i / (events.length - 1);
            return (<div className="rt-tl__event" key={e.t}>
                <motion.span className="rt-tl__dot" initial={{ scale: 0 }} animate={{ scale: drawn ? 1 : 0 }} transition={{
                    delay: 0.2 + index * 0.15 + frac * 1.5,
                    type: 'spring',
                    stiffness: 320,
                    damping: 20,
                }}/>
                <motion.div className="rt-tl__meta" initial={{ opacity: 0, y: 8 }} animate={drawn ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }} transition={{ delay: 0.35 + index * 0.15 + frac * 1.5, duration: 0.4 }}>
                  <span className="rt-tl__time">{e.t}</span>
                  <span className="rt-tl__label">{e.label}</span>
                </motion.div>
              </div>);
        })}
        </div>
      </div>
    </Reveal>);
}
export function Returns() {
    const marquee = useMarqueeSpeed();
    // Compound chart draws when scrolled into view — useInView + explicit `animate`,
    // because a nested whileInView inside the <Reveal> parent doesn't fire (the parent's
    // variant propagation overrides it; same reason as ScenarioTimeline above).
    const compRef = useRef(null);
    const compDrawn = useInView(compRef, viewportOnce);
    const th = useT('returns.hero');
    const tm = useT('returns.marquee');
    const tt = useT('returns.trust');
    const te = useT('returns.entry');
    const tcr = useT('returns.credit');
    const claim = useClaim();
    const tco = useT('returns.compound');
    const tv = useT('returns.verify');
    const tn = useT('returns.notify');
    const PRINCIPLES = PRINCIPLE_KEYS.map((k) => tm(k));
    const calcConfig = useMemo(() => {
        const max = new Date();
        max.setHours(0, 0, 0, 0);
        max.setDate(max.getDate() - 1); // yesterday
        const min = new Date(2020, 0, 1);
        let start = new Date(max);
        start.setFullYear(start.getFullYear() - 3); // default: a 3-year window
        if (start < min)
            start = new Date(min);
        return {
            sectionId: 'returns-calc',
            textSection: 'returns.calc',
            annualRate: 0.45,
            reinvestAffectsCalc: true,
            dateMin: min,
            dateMax: max,
            minPeriodMonths: 12,
            defaultStart: start,
            defaultEnd: max,
        };
    }, []);
    const loop = [...PRINCIPLES, ...PRINCIPLES, ...PRINCIPLES, ...PRINCIPLES];
    return (<>
      <Navbar />
      <main className="rt">
        {/* ============ 01 · Hero (dark) ============ */}
        <section className="rt-hero">
          <div className="rt-hero__bg" aria-hidden="true">
            <div className="rt-hero__grid anim-grid"/>
          </div>
          <div className="container rt-hero__inner">
            <div className="rt-hero__copy">
              <motion.span className="rt-hero__badge" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: easeOut }}>
                <TrendingUp size={15}/>
                {th('badge')}
              </motion.span>
              <motion.h1 className="rt-hero__title" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: easeOut }}>
                {th.rich('title')}
              </motion.h1>
              <motion.div className="rt-hero__figure" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18, ease: easeOut }}>
                <span className="rt-hero__num num">
                  <ClaimCount duration={1.8} delay={0.4}/>
                </span>
                <span className="rt-hero__num-cap">{th.rich('num_cap')}</span>
              </motion.div>
              <motion.p className="rt-hero__lead" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3, ease: easeOut }}>
                {th.rich('lead')}
              </motion.p>
            </div>

            <motion.div className="rt-hero__visual" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.35, ease: easeOut }}>
              <HeroChart />
            </motion.div>
          </div>

          <motion.a className="rt-hero__scroll" href="#returns-calc" aria-label="До калькулятора" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
            <ChevronDown size={20}/>
          </motion.a>
        </section>

        {/* ============ 02 · Calculator (reused) ============ */}
        <Calculator config={calcConfig}/>

        {/* ============ 03 · Principles marquee ============ */}
        <section className="rt-marquee-sec" aria-label="Принципи стратегії">
          <div className="rt-marquee" ref={marquee.ref} onMouseEnter={marquee.onMouseEnter} onMouseLeave={marquee.onMouseLeave}>
            <div className="rt-marquee__track" aria-hidden="true">
              {loop.map((p, i) => (<span className="rt-marquee__item" key={i}>
                  <img src={smallSign} className="rt-marquee__sign" width={18} height={18} alt="" aria-hidden="true"/>
                  {p}
                </span>))}
            </div>
          </div>
        </section>

        {/* ============ 04 (01) · Trust ============ */}
        <section className="section">
          <div className="container">
            <SectionHead num="01" title={tt.rich('title')}/>
            <div className="rt-trust">
              {TRUST_ICONS.map((Icon, i) => {
            return (<Reveal className="rt-trust-card" variant="fadeUp" delay={i * 0.08} key={i}>
                    <span className="rt-icon">
                      <Icon size={24}/>
                    </span>
                    <h3 className="rt-trust-card__title">{tt.rich(`c${i + 1}_title`)}</h3>
                    <p className="rt-trust-card__text">{tt.rich(`c${i + 1}_text`)}</p>
                  </Reveal>);
        })}
            </div>
          </div>
        </section>

        {/* ============ 05 (02) · Entry-point scenarios ============ */}
        <section className="section">
          <div className="container">
            <SectionHead num="02" title={te.rich('title')} sub={te.rich('sub')}/>
            <div className="rt-scenarios">
              {SCENARIO_N.map((n, i) => (<ScenarioTimeline key={n} tag={te(`s${n}_tag`)} events={EVENT_N.map((e) => ({ t: te(`s${n}_e${e}_t`), label: te(`s${n}_e${e}`) }))} index={i}/>))}
            </div>
            <Reveal className="rt-scenarios__note" variant="fadeUp">
              <p>{te.rich('note')}</p>
            </Reveal>

            <div className="rt-concl">
              <Reveal className="rt-concl__statement" variant="fadeBlur">
                <p className="rt-concl__big">{te.rich('concl', { accent: 'rt-concl__hl' })}</p>
              </Reveal>
              <div className="rt-concl__cards">
                <Reveal className="rt-concl-card" variant="fadeUp">
                  <span className="rt-icon rt-icon--sm">
                    <TrendingUp size={20}/>
                  </span>
                  <h3 className="rt-concl-card__title">{te.rich('rules_title')}</h3>
                  <p className="rt-concl-card__text">{te.rich('rules_text')}</p>
                  <div className="rt-concl-card__rules">
                    <span className="rt-rule rt-rule--buy">
                      <ArrowUpRight size={15}/>
                      {te('rule_buy')}
                    </span>
                    <span className="rt-rule rt-rule--sell">
                      <Wallet size={15}/>
                      {te('rule_sell')}
                    </span>
                  </div>
                </Reveal>
                <Reveal className="rt-concl-card" variant="fadeUp" delay={0.08}>
                  <span className="rt-icon rt-icon--sm">
                    <Calendar size={20}/>
                  </span>
                  <h3 className="rt-concl-card__title">{te.rich('dt_title')}</h3>
                  <p className="rt-concl-card__text">{te.rich('dt_text')}</p>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 06 (03) · Credit leverage (navy) ============ */}
        <section className="section">
          <div className="container">
            <Reveal className="feature-card rt-credit" variant="fadeUp">
              <span className="feature-card__grid" aria-hidden="true"/>
              <div className="rt-credit__grid2">
                <div className="rt-credit__copy">
                  <span className="feature-card__badge">{tcr('badge')}</span>
                  <h2 className="feature-card__title">{tcr.rich('title')}</h2>
                  <p>{tcr.rich('text')}</p>
                  {claim && (<div className="rt-credit__formula num" aria-label={`${claim.num} відсотків мінус 6 відсотків — приблизно плюс ${+(claim.num - 6).toFixed(2)} відсотків додатково`}>
                    <span>{claim.text}</span>
                    <span className="rt-credit__op">−</span>
                    <span>6%</span>
                    <span className="rt-credit__op">≈</span>
                    <span className="rt-credit__res">+{+(claim.num - 6).toFixed(2)}%</span>
                  </div>)}
                  <p className="rt-credit__fine">{tcr.rich('fine')}</p>
                </div>
                <div className="rt-credit__art">
                  <img src={CREDIT_ART} alt="Кредитне плече 1:2 — важіль, що підсилює дохідність"/>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ============ 07 (04) · Compound interest ============ */}
        <section className="section">
          <div className="container">
            <SectionHead num="04" title={tco.rich('title')}/>
            <div className="rt-comp">
              <Reveal className="rt-comp__viz" variant="fadeUp">
                <div className="rt-comp__chart" ref={compRef} role="img" aria-label="12 стовпчиків місяців: складний відсоток зростає до +42.6% проти +36% простого">
                  {/* y-axis scale */}
                  {COMP_TICKS.map((t) => (<div className="rt-comp__gl" key={t} style={{ bottom: `${(t / COMP_TOP) * 100}%` }}>
                      <span>{Math.round(t * 100)}%</span>
                    </div>))}
                  {/* simple-interest reference at 36% — label kept left, over the short bars */}
                  <div className="rt-comp__simple" style={{ bottom: `${(0.36 / COMP_TOP) * 100}%` }}>
                    <span className="rt-comp__simple-lbl">простий % · +36%</span>
                  </div>
                  {/* compound peak at 42.6% — where the tallest bar tops out */}
                  <motion.div className="rt-comp__peak" style={{ bottom: `${(COMPOUND_MAX / COMP_TOP) * 100}%` }} initial={{ opacity: 0 }} animate={{ opacity: compDrawn ? 1 : 0 }} transition={{ delay: 1, duration: 0.4 }}>
                    <span className="rt-comp__peak-lbl num">+42.6%</span>
                  </motion.div>
                  {COMPOUND.map((v, i) => (<div className="rt-comp__col" key={i}>
                      <motion.div className="rt-comp__bar" style={{ height: `${((v - 1) / COMP_TOP) * 100}%` }} initial={{ scaleY: 0 }} animate={{ scaleY: compDrawn ? 1 : 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.5, ease: easeOut }}/>
                      <span className="rt-comp__m">{i + 1}</span>
                    </div>))}
                </div>
                <span className="rt-comp__axis">{tco('axis')}</span>
              </Reveal>

              <div className="rt-comp__qa">
                <Reveal className="rt-qa__row" variant="fadeUp">
                  <span className="rt-qa__badge rt-qa__badge--q">
                    <HelpCircle size={15}/>
                    {tco('q_badge')}
                  </span>
                  <p>{tco.rich('q')}</p>
                </Reveal>
                <Reveal className="rt-qa__row" variant="fadeUp" delay={0.1}>
                  <span className="rt-qa__badge rt-qa__badge--a">
                    <Check size={15}/>
                    {tco('a_badge')}
                  </span>
                  <p>{tco.rich('a')}</p>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 08 (05) · Verify on virtual trades ============ */}
        <section className="section">
          <div className="container">
            <Reveal className="head-center" variant="fadeBlur">
              <h2 className="head-center__title">{tv.rich('title')}</h2>
              <p className="head-center__sub">{tv.rich('sub')}</p>
            </Reveal>
            <div className="rt-verify-a">
              <ReturnsGraph />
              <Reveal className="rt-tip" variant="fadeUp" delay={0.05}>
                <span className="rt-icon rt-icon--sm">
                  <Eye size={20}/>
                </span>
                <div className="rt-tip__body">
                  <span className="rt-tip__k">{tv('tip_badge')}</span>
                  <p>{tv.rich('tip')}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <VirtualTrades />

        {/* Notifications */}
        <section className="section">
          <div className="container">
            <Reveal className="rt-notify" variant="fadeUp">
              <div className="rt-notify__mock" aria-hidden="true">
                <div className="rt-noti rt-noti--mail">
                  <span className="rt-noti__icon">
                    <Mail size={16}/>
                  </span>
                  <div className="rt-noti__body">
                    <span className="rt-noti__from">Trading.com.ua</span>
                    <span className="rt-noti__text">{tn('mail_text')}</span>
                  </div>
                  <span className="rt-noti__pct num">+9.7%</span>
                </div>
                <div className="rt-noti rt-noti--tg">
                  <span className="rt-noti__icon">
                    <Send size={16}/>
                  </span>
                  <div className="rt-noti__body">
                    <span className="rt-noti__from">Telegram</span>
                    <span className="rt-noti__text">{tn('tg_text')}</span>
                  </div>
                  <span className="rt-noti__pct num">+10.2%</span>
                </div>
              </div>
              <div className="rt-notify__copy">
                <h3 className="rt-notify__title">{tn.rich('title')}</h3>
                <p>{tn.rich('text')}</p>
                <div className="rt-notify__chips">
                  <span className="rt-notify__chip">
                    <Mail size={15}/>
                    Email
                  </span>
                  <span className="rt-notify__chip">
                    <Send size={15}/>
                    Telegram
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </>);
}
